// Task #16: Error indicator component for confidence-based error handling
import React, { useState } from 'react';
import logger from '../utils/logger';
import {
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Flag,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { ConfidenceError, getErrorDisplayInfo } from '../types';
import { useStore } from '../store/useStore';

interface ErrorIndicatorProps {
  errors: ConfidenceError[];
  caseNumber?: string;
  compact?: boolean;
  showRecoveryActions?: boolean;
}

const ErrorIndicator: React.FC<ErrorIndicatorProps> = ({
  errors,
  caseNumber,
  compact = false,
  showRecoveryActions = true,
}) => {
  const {
    retryFailedOperation,
    removeConfidenceError,
    clearErrorsForCase,
    isRecoveringFromErrors,
  } = useStore();
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());
  const [retryingErrors, setRetryingErrors] = useState<Set<string>>(new Set());

  if (!errors || errors.length === 0) {
    return null;
  }

  const toggleErrorExpansion = (errorId: string) => {
    const newExpanded = new Set(expandedErrors);
    if (newExpanded.has(errorId)) {
      newExpanded.delete(errorId);
    } else {
      newExpanded.add(errorId);
    }
    setExpandedErrors(newExpanded);
  };

  const handleRetryError = async (error: ConfidenceError) => {
    if (!error.recoverable || retryingErrors.has(error.id)) return;

    setRetryingErrors((prev) => new Set(prev).add(error.id));

    try {
      const success = await retryFailedOperation(error.id);
      if (success) {
        logger.debug('Successfully retried operation:', error.errorType);
      } else {
        logger.warn('Retry failed for operation:', error.errorType);
      }
    } catch (retryError) {
      logger.error('Error during retry:', retryError);
    } finally {
      setRetryingErrors((prev) => {
        const newSet = new Set(prev);
        newSet.delete(error.id);
        return newSet;
      });
    }
  };

  const handleDismissError = (errorId: string) => {
    removeConfidenceError(errorId);
  };

  const handleClearAllCaseErrors = () => {
    if (caseNumber) {
      clearErrorsForCase(caseNumber);
    }
  };

  const getErrorIcon = (error: ConfidenceError) => {
    switch (error.severity) {
      case 'critical':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-blue-600" />;
    }
  };

  const getErrorStyles = (error: ConfidenceError) => {
    switch (error.severity) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'error':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  // Compact mode for case list table
  if (compact) {
    const criticalErrors = errors.filter((e) => e.severity === 'critical').length;
    const totalErrors = errors.length;

    return (
      <div className="flex items-center space-x-1">
        {criticalErrors > 0 ? (
          <div title={`${criticalErrors} critical errors`}>
            <AlertCircle className="w-4 h-4 text-red-600" />
          </div>
        ) : (
          <div title={`${totalErrors} errors`}>
            <AlertTriangle className="w-4 h-4 text-orange-600" />
          </div>
        )}
        <span className="text-xs text-gray-600">{totalErrors}</span>
      </div>
    );
  }

  // Full error display mode
  return (
    <div className="space-y-2">
      {/* Error Summary Header */}
      {errors.length > 1 && (
        <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="font-medium text-gray-900">
              {errors.length} Error{errors.length > 1 ? 's' : ''} Detected
            </span>
          </div>
          {showRecoveryActions && caseNumber && (
            <button
              onClick={handleClearAllCaseErrors}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Clear All
            </button>
          )}
        </div>
      )}

      {/* Individual Error Cards */}
      {errors.map((error) => {
        const displayInfo = getErrorDisplayInfo(error);
        const isExpanded = expandedErrors.has(error.id);
        const isRetrying = retryingErrors.has(error.id);
        const canRetry = error.recoverable && !isRetrying && !isRecoveringFromErrors;

        return (
          <div key={error.id} className={`border rounded-lg ${getErrorStyles(error)}`}>
            {/* Error Header */}
            <div className="p-3">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2">
                  {getErrorIcon(error)}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium">{displayInfo.title}</h4>
                    <p className="text-sm opacity-90 mt-1">{displayInfo.message}</p>

                    {/* Error Meta Information */}
                    <div className="flex items-center space-x-4 mt-2 text-xs opacity-75">
                      <span>Code: {error.errorCode}</span>
                      <span>Type: {error.errorType.replace(/_/g, ' ')}</span>
                      {error.caseNumber && <span>Case: {error.caseNumber}</span>}
                      <span>
                        Retries: {error.retryCount}/{error.maxRetries}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-1 ml-2">
                  {/* Expand/Collapse Button */}
                  <button
                    onClick={() => toggleErrorExpansion(error.id)}
                    className="p-1 hover:bg-black hover:bg-opacity-10 rounded"
                    title={isExpanded ? 'Show less' : 'Show more'}
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {/* Dismiss Button */}
                  <button
                    onClick={() => handleDismissError(error.id)}
                    className="p-1 hover:bg-black hover:bg-opacity-10 rounded"
                    title="Dismiss error"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Recovery Actions */}
              {showRecoveryActions && (
                <div className="flex items-center space-x-2 mt-3">
                  {canRetry && (
                    <button
                      onClick={() => handleRetryError(error)}
                      disabled={isRetrying}
                      className="flex items-center space-x-1 px-3 py-1 bg-white border border-current rounded text-sm hover:bg-black hover:bg-opacity-5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw
                        className={`w-3 h-3 ${isRetrying ? 'animate-spin' : ''}`}
                      />
                      <span>{isRetrying ? 'Retrying...' : 'Retry'}</span>
                    </button>
                  )}

                  {!error.recoverable && (
                    <span className="text-xs opacity-75 italic">
                      Manual intervention required
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Expanded Error Details */}
            {isExpanded && (
              <div className="border-t border-current border-opacity-20 p-3 bg-black bg-opacity-5">
                <div className="space-y-2 text-sm">
                  {/* Detailed Error Context */}
                  {error.context && (
                    <div>
                      <span className="font-medium">Operation:</span>{' '}
                      {error.context.operation}
                      {error.context.parameters && (
                        <div className="mt-1 pl-4">
                          <span className="font-medium">Parameters:</span>
                          <pre className="text-xs mt-1 p-2 bg-black bg-opacity-10 rounded overflow-x-auto">
                            {JSON.stringify(error.context.parameters, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Retry Strategy */}
                  <div>
                    <span className="font-medium">Retry Strategy:</span>{' '}
                    {error.retryStrategy.type}
                    <div className="text-xs opacity-75 mt-1">
                      Base delay: {error.retryStrategy.baseDelay}ms, Max delay:{' '}
                      {error.retryStrategy.maxDelay}ms
                      {error.retryStrategy.jitter && ', with jitter'}
                    </div>
                  </div>

                  {/* Recovery Information */}
                  {error.recovery && (
                    <div>
                      <span className="font-medium">Recovery:</span>{' '}
                      {error.recovery.fallbackBehavior}
                      <div className="text-xs opacity-75 mt-1">
                        {error.recovery.userMessage}
                      </div>
                    </div>
                  )}

                  {/* Timestamp */}
                  <div className="text-xs opacity-75">
                    <span className="font-medium">Occurred:</span>{' '}
                    {new Date(error.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ErrorIndicator;
